import {
  Flex,
  FlexProps,
  IconButton,
  Input,
  ResponsiveValue,
  TextProps,
  Tooltip,
} from '@chakra-ui/react';
import { MinusIcon, PlusIcon } from '@sphere/icons';
import { rem } from 'polished';
import { ForwardedRef, forwardRef, useEffect, useState } from 'react';
import { useDebounce } from 'react-use';

export interface QuantityPickerProps extends Omit<FlexProps, 'onChange'> {
  onChange?: (quantity: number) => void;
  onDebouncedChange?: (quantity: number) => void;
  onRemove?: () => void;
  value?: number;
  min?: number;
  max?: number;
  step?: number;
  size?: ResponsiveValue<string | number> | string | number;
  iconSize?: ResponsiveValue<string | number> | string | number;
  textStyles?: TextProps;
  disabled?: boolean;
  tooltipText?: string;
}

export const QuantityPicker = forwardRef(
  (
    {
      onChange,
      onDebouncedChange,
      onRemove,
      value = 1,
      min = 1,
      max,
      step = 1,
      size = rem(30),
      iconSize = rem(10),
      textStyles,
      disabled,
      tooltipText,
      ...props
    }: QuantityPickerProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const [quantity, setQuantity] = useState(value);
    const [inputValue, setInputValue] = useState<string>(String(value));

    useDebounce(
      () => {
        if (quantity < min) return onRemove?.();
        if (quantity === min) return setQuantity(min);
        if (max && quantity > max) return setQuantity(max);
        onDebouncedChange?.(quantity);
      },
      500,
      [quantity],
    );

    useEffect(() => {
      if (quantity < min) return;
      if (max && quantity > max) return;
      onChange?.(quantity);
      setInputValue(String(quantity));
    }, [quantity]);

    const handleIncrement = () => {
      if (max && quantity >= max) return;
      setQuantity(prevState => prevState + step);
    };

    const handleDecrement = () => {
      if (quantity <= min) return onRemove?.();
      setQuantity(prevState => prevState - step);
    };

    const handleChange = (newValue: string) => {
      setInputValue(newValue);

      const numberValue = Number(newValue);
      if (numberValue < min) return onRemove?.();
      if (max && numberValue > max) return;
      setQuantity(numberValue);
    };

    useEffect(() => {
      setQuantity(value);
    }, [value]);

    const isDecrementDisabled = disabled || (quantity <= min && !onRemove);
    const isIncrementDisabled = disabled || (!!max && quantity >= max);

    return (
      <Flex
        h={size}
        display="inline-flex"
        bg="gray.4"
        border="1px solid"
        borderColor="gray.2"
        rounded="radius.4"
        align="center"
        cursor={disabled ? 'not-allowed' : 'unset'}
        ref={ref}
        {...props}
      >
        <IconButton
          aria-label="Decrement"
          p={0}
          variant="clear"
          h="100%"
          w={size}
          minW="unset"
          onClick={handleDecrement}
          isDisabled={isDecrementDisabled}
          icon={<MinusIcon boxSize={iconSize} />}
          transition="opacity 160ms ease-in-out"
          _hover={{ opacity: 0.6 }}
          _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
          zIndex={1}
        />
        <Input
          type="number"
          inputMode="numeric"
          pattern="[0-9]*"
          fontSize="xs"
          textAlign="center"
          fontWeight="medium"
          border="none"
          boxSize={size}
          mx={{ base: 'space.-8', md: 'space.-12' }}
          p={0}
          bg="unset"
          _focus={{ border: 'none' }}
          _focusVisible={{ border: 'none' }}
          onChange={e => handleChange(e.currentTarget.value)}
          onBlur={() => handleChange(String(value))}
          onClick={e => (e.target as HTMLInputElement).select()}
          value={inputValue}
          zIndex={0}
          {...textStyles}
        />
        <Tooltip
          label={tooltipText}
          placement="top"
          hasArrow
          arrowSize={8}
          isDisabled={!tooltipText || !isIncrementDisabled}
        >
          <IconButton
            aria-label="Increment"
            p={0}
            variant="clear"
            h="100%"
            w={size}
            minW="unset"
            onClick={handleIncrement}
            isDisabled={disabled || (!!max && quantity >= max)}
            icon={<PlusIcon boxSize={iconSize} />}
            transition="opacity 160ms ease-in-out"
            _hover={{ opacity: 0.6 }}
            _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
            zIndex={1}
          />
        </Tooltip>
      </Flex>
    );
  },
);
